import React, { useState, useEffect, useRef } from "react";
import SEO from "./components/SEO";
import LocalBusinessSchema from "./components/LocalBusinessSchema";
import FAQSchema, { faqs } from "./components/FAQSchema";
import PropTypes from "prop-types";

// --- Ícones SVG ---
const DollarSignIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);

const ShieldIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

const HomeIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

const ZapIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
);

const MenuIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const XIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// --- Dados da Aplicação ---
const features = [
  {
    icon: <DollarSignIcon className="w-10 h-10 text-yellow-400" />,
    title: "Economia Garantida",
    description:
      "Reduza sua conta de luz em até 95% e fique livre da inflação energética.",
  },
  {
    icon: <ShieldIcon className="w-10 h-10 text-green-400" />,
    title: "Proteção Contra Aumentos",
    description:
      "Fique imune aos constantes e imprevisíveis aumentos na tarifa de energia elétrica.",
  },
  {
    icon: <HomeIcon className="w-10 h-10 text-blue-400" />,
    title: "Valorização do Imóvel",
    description:
      "Imóveis com sistema de energia solar são mais valorizados no mercado.",
  },
  {
    icon: <ZapIcon className="w-10 h-10 text-orange-400" />,
    title: "Instalação Rápida e Segura",
    description:
      "Nossa equipe de especialistas garante uma instalação eficiente e sem dores de cabeça.",
  },
];

const testimonials = [
  {
    quote:
      "Aqui em casa a conta vinha quase sempre acima de R$600. Depois dos painéis ficou entre R$70 e R$90 na maior parte do ano. Não foi de um dia pro outro, mas já na segunda fatura a diferença apareceu.",
    name: "Carlos Mendes",
    location: "Araçatuba - SP",
  },
  {
    quote:
      "Eu tinha receio por causa do investimento inicial. Eles mostraram a projeção, instalei em março e agora (uns 8 meses) já economizei mais do que esperava. Atendimento simples, sem empurrar coisa a mais.",
    name: "Luciana Prado",
    location: "Birigui - SP",
  },
  {
    quote:
      "Instalaram em dois dias. Antes a conta variava demais, agora fica estável e previsível. Quando tive uma dúvida sobre monitoramento me responderam no mesmo dia pelo WhatsApp.",
    name: "Rogério Almeida",
    location: "Lins - SP",
  },
  {
    quote:
      "Achei que fosse barulho e obra por uma semana, mas foi bem mais tranquilo. A geração está próxima do que foi prometido (às vezes até um pouco acima em dias bem limpos). Acompanhar pelo app vicia.",
    name: "Patrícia Souza",
    location: "Araçatuba - SP",
  },
];

const WHATSAPP_URL = "https://wa.me/18997998501?text=Vim%20pelo%20site%20gostaria%20de%20fazer%20um%20or%C3%A7amento%20gratu%C3%ADto";

// --- Hook customizado para animação de scroll ---
const useAnimateOnScroll = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const animationClasses = `transition-all duration-1000 ease-in-out ${
    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
  }`;

  return [ref, animationClasses];
};

// --- Componente Principal da Aplicação ---
export default function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [featuresRef, featuresAnimation] = useAnimateOnScroll();
  const [testimonialsRef, testimonialsAnimation] = useAnimateOnScroll();
  const [contactRef, contactAnimation] = useAnimateOnScroll();

  // Efeito de brilho nos cards
  const handleCardMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    card.style.setProperty("--mouse-x", `${x}px`);
    card.style.setProperty("--mouse-y", `${y}px`);
  };

  return (
    <div className="bg-gray-900 text-gray-200 font-sans">
      {/* SEO da Home */}
      <SEO
        title="Sua Conta de Luz sob Controle"
        description="Energia solar em Araçatuba e interior de São Paulo: reduza sua conta em até 95% com instalação rápida, projeto otimizado e suporte especializado."
        image="https://solarsystembrasil.com.br/banner.jpg"
        url="https://solarsystembrasil.com.br/"
        canonical="https://solarsystembrasil.com.br/"
        type="website"
        keywords={[
          "energia solar",
          "economia de energia",
          "painéis solares",
          "Araçatuba",
          "orçamento energia solar",
          "instalação fotovoltaica",
          "energia solar interior de São Paulo",
          "Birigui energia solar",
          "Lins energia solar"
        ]}
      />
      <LocalBusinessSchema />
      <FAQSchema />

      {/* Header */}
      <header className="bg-gray-900/70 backdrop-blur-lg shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <img
            src="/logo.png"
            alt="Logo Solar System Brasil"
            className="h-10 w-auto"
          />

          <nav className="hidden md:flex space-x-8">
            <a
              href="#features"
              className="text-gray-300 hover:text-yellow-400 transition-colors"
            >
              Vantagens
            </a>
            <a
              href="#testimonials"
              className="text-gray-300 hover:text-yellow-400 transition-colors"
            >
              Depoimentos
            </a>
            <a
              href="#contact"
              className="text-gray-300 hover:text-yellow-400 transition-colors"
            >
              Contato
            </a>
          </nav>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-block bg-yellow-500 text-gray-900 font-bold py-2 px-6 rounded-lg hover:bg-yellow-400 transition-all duration-300 transform hover:scale-105"
            aria-label="Solicitar orçamento gratuito via WhatsApp"
          >
            Orçamento Gratuito
          </a>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-gray-300 z-50"
          >
            {isMenuOpen ? (
              <XIcon className="w-6 h-6" />
            ) : (
              <MenuIcon className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Menu Mobile */}
        <div
          className={`md:hidden absolute top-full left-0 w-full bg-gray-900/95 backdrop-blur-lg transition-all duration-300 ease-in-out transform ${
            isMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          } overflow-hidden`}
        >
          <nav className="flex flex-col items-center space-y-4 py-8">
            <a
              href="#features"
              onClick={() => setIsMenuOpen(false)}
              className="text-gray-300 text-lg hover:text-yellow-400 transition-colors"
            >
              Vantagens
            </a>
            <a
              href="#testimonials"
              onClick={() => setIsMenuOpen(false)}
              className="text-gray-300 text-lg hover:text-yellow-400 transition-colors"
            >
              Depoimentos
            </a>
            <a
              href="#contact"
              onClick={() => setIsMenuOpen(false)}
              className="text-gray-300 text-lg hover:text-yellow-400 transition-colors"
            >
              Contato
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMenuOpen(false)}
              className="mt-4 bg-yellow-500 text-gray-900 font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-all duration-300"
              aria-label="Solicitar orçamento gratuito via WhatsApp"
            >
              Orçamento Gratuito
            </a>
          </nav>
        </div>
      </header>

      <main>
        {/* Seção Hero */}
        <section className="relative h-[90vh] min-h-[600px] flex items-center text-white overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('/installation-solar-panels-undp.png')",
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent"></div>
          <div className="container mx-auto px-6 text-center relative z-10">
            <h1 className="text-4xl md:text-7xl font-black leading-tight mb-4 uppercase">
              Sua Conta de Luz{" "}
              <span className="text-yellow-400">sob Controle</span>
            </h1>
            <p className="text-lg md:text-xl max-w-3xl mx-auto mb-8 text-gray-300">
              Cansado de contas de luz altas? Instale painéis solares e reduza
              seus gastos com energia em até 95%.
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-yellow-500 text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-all duration-300 md:transform md:hover:scale-105 shadow-lg shadow-yellow-500/20
              w-full max-w-[360px] mx-auto md:mx-0
              inline-flex items-center justify-center text-center
              text-base md:text-lg leading-tight whitespace-normal break-words
              px-6 py-3 md:px-10 md:py-4
              max-[380px]:text-sm max-[380px]:px-5
              max-[340px]:text-sm max-[340px]:px-4
              max-[330px]:text-xs max-[330px]:px-4 max-[330px]:py-3
              min-h-[52px]"
              aria-label="Fazer orçamento gratuito via WhatsApp"
            >
              Faça já um orçamento gratuito
            </a>
          </div>
        </section>

        {/* Seção de Vantagens */}
        <section
          id="features"
          ref={featuresRef}
          className={`py-24 bg-black ${featuresAnimation}`}
        >
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white">
                O Fim das Contas de Luz Altas
              </h2>
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
                Descubra como a energia solar pode blindar seu orçamento contra
                os aumentos da tarifa.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature) => (
                <article
                  key={feature.title}
                  onMouseMove={handleCardMouseMove}
                  aria-label={feature.title}
                  className="feature-card bg-gray-800/50 p-8 rounded-2xl border border-gray-700 transition-all duration-300 flex flex-col items-center text-center transform hover:-translate-y-2 relative overflow-hidden hover:border-yellow-500/50"
                >
                  <div className="bg-gray-900 p-4 rounded-full mb-6 border-2 border-gray-700 z-10">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-white z-10">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 z-10">{feature.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Seção de Depoimentos */}
        <section
          id="testimonials"
          ref={testimonialsRef}
          className={`py-24 bg-gray-900 ${testimonialsAnimation}`}
        >
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white">
                O que nossos clientes dizem
              </h2>
              <p className="text-gray-400 mt-4">
                A satisfação de quem já economiza com a energia do sol.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.name}
                  className="bg-gray-800 p-8 rounded-lg shadow-lg border border-transparent hover:border-blue-400 transition-colors"
                >
                  <p className="text-gray-300 italic mb-6 text-lg">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4 ring-2 ring-blue-400/50">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-white">{testimonial.name}</p>
                      <p className="text-sm text-gray-400">
                        {testimonial.location}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-gray-900" id="faq">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">Perguntas Frequentes sobre Energia Solar</h2>
            <div className="space-y-5">
              {faqs.map((item) => (
                <div key={item.question} className="border border-gray-700 rounded-lg p-6 bg-gray-800/60">
                  <h3 className="text-xl font-semibold text-yellow-400 mb-3">{item.question}</h3>
                  <p className="text-gray-300 leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 text-center">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-yellow-500 text-gray-900 font-bold py-3 px-8 rounded-lg text-lg hover:bg-yellow-400 transition-all duration-300 transform hover:scale-105 shadow-lg"
                aria-label="Tirar dúvidas sobre energia solar em Araçatuba"
              >
                Tire suas dúvidas pelo WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* Seção Instagram (estática) */}
        <section id="instagram" className="py-24 bg-black/90 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(circle_at_30%_20%,rgba(255,200,0,0.15),transparent_60%),radial-gradient(circle_at_80%_70%,rgba(255,140,0,0.12),transparent_55%)]" />
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-14 max-w-3xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Acompanhe a gente no Instagram
              </h2>
              <p className="text-gray-400">
                Fotos reais de instalações, bastidores e resultados de geração. Transparência todo dia no perfil.
              </p>
            </div>
            {/* Grid estática de posts (imagens locais) */}
            <div className="mb-12">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 md:gap-5">
                {[
                  "501453443_18279921961255645_8629067644873239042_n.jpg",
                  "505420957_18281759998255645_1217852126023119198_n.jpg",
                  "509632509_18283078126255645_874964743062613287_n.jpg",
                  "515266502_18283735183255645_2445517845210830847_n.jpg",
                  "517347298_18284629285255645_7446217075066976064_n.jpg",
                  "523393445_18286005460255645_3865348917651203938_n.jpg",
                ].map((file, i) => (
                  <a
                    key={file}
                    href="https://www.instagram.com/solarsystembrasil/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative aspect-square rounded-xl overflow-hidden bg-gray-800/70 border border-gray-700/60 hover:border-yellow-400/60 transition-colors"
                    aria-label={`Abrir Instagram Solar System Brasil - imagem ${i + 1}`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-900/40 via-gray-900/5 to-gray-900/60" />
                    <img
                      loading="lazy"
                      src={`/instagram_posts/${file}`}
                      alt="Post do Instagram Solar System Brasil"
                      className="h-full w-full object-cover object-center scale-105 group-hover:scale-110 transition-transform duration-500 ease-out"
                    />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-end p-3">
                      <span className="text-xs font-medium text-yellow-400 tracking-wide">VER MAIS</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            <div className="text-center">
              <a
                href="https://www.instagram.com/solarsystembrasil/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-gray-900 font-semibold py-3 px-8 rounded-full shadow-lg shadow-yellow-500/20 hover:shadow-yellow-400/30 hover:scale-[1.03] transition-all"
                aria-label="Seguir Solar System Brasil no Instagram"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <circle cx="17.5" cy="6.5" r="1" />
                </svg>
                <span>@solarsystembrasil</span>
              </a>
            </div>
          </div>
        </section>

        {/* Seção de CTA (Call to Action) */}
        <section
          id="contact"
          ref={contactRef}
          className={`py-24 bg-gradient-to-r from-yellow-500 to-orange-500 ${contactAnimation}`}
        >
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900">
              Pronto para começar a economizar?
            </h2>
            <p className="max-w-2xl mx-auto mb-8 text-gray-800 text-lg">
              É rápido, fácil e sem compromisso. Nossa equipe entrará em contato
              com uma proposta personalizada para você.
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gray-900 text-white font-bold py-4 px-10 rounded-lg text-lg hover:bg-black transition-all duration-300 transform hover:scale-105 shadow-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800"
              aria-label="Solicitar orçamento gratuito via WhatsApp"
            >
              Quero meu orçamento gratuito!
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-black text-white py-10">
        <div className="container mx-auto px-6 text-center">
          <img
            src="/logo.png"
            alt="Logo Solar System Brasil"
            className="h-8 w-auto mx-auto mb-4"
          />
          <p className="mb-4 text-gray-400">
            Economia inteligente para o seu bolso.
          </p>
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Solar System Brasil. Todos os
            direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

// PropTypes para ícones SVG (classe opcional)
DollarSignIcon.propTypes = { className: PropTypes.string };
ShieldIcon.propTypes = { className: PropTypes.string };
HomeIcon.propTypes = { className: PropTypes.string };
ZapIcon.propTypes = { className: PropTypes.string };
MenuIcon.propTypes = { className: PropTypes.string };
XIcon.propTypes = { className: PropTypes.string };
